import { Injectable } from '@angular/core';
import * as go from 'src/libs/gojs/release/go';

/**
 * Defines all the gojs shapes we use, some of which are copied from RoundedRectangles.ts and Figures.ts.
 *
 * Note that we include some of the internal classes and functions in Figures.ts in order to make the shapes available
 */
@Injectable({
  providedIn: 'root'
})
export class GojsShapesService {

  constructor() { }

  init() {
    this.initCustom();
    this.initRectangles();
    this.initFigures();
  }

  private initCustom() {
    // Just top and bottom sides of a rectangle, replace this with a parameterised one that indicates which sides to have.
    go.Shape.defineFigureGenerator("TopBottomRectangle", function(shape, w, h) {
      return new go.Geometry()
        .add(new go.PathFigure(0, 0, false)
          .add(new go.PathSegment(go.PathSegment.Line, w, 0))
        )
        .add(new go.PathFigure(0, h, false)
          .add(new go.PathSegment(go.PathSegment.Line, w, h))
        );
    });

    // Just top side of a rectangle, replace this with a parameterised one that indicates which sides to have.
    go.Shape.defineFigureGenerator("TopRectangle", function(shape, w, h) {
      return new go.Geometry()
        .add(new go.PathFigure(0, 0, false)
          .add(new go.PathSegment(go.PathSegment.Line, w, 0))
        );
    });

    // Just bottom side of a rectangle, replace this with a parameterised one that indicates which sides to have.
    go.Shape.defineFigureGenerator("BottomRectangle", function(shape, w, h) {
      return new go.Geometry()
        .add(new go.PathFigure(0, h, false)
          .add(new go.PathSegment(go.PathSegment.Line, w, h))
        );
    });

  }

  // Copied from RoundedRectangles.ts
  private initRectangles() {

    go.Shape.defineFigureGenerator('RoundedTopRectangle', function (shape: go.Shape, w: number, h: number) {
      // this figure takes one parameter, the size of the corner
      let p1 = 5;  // default corner size
      if (shape !== null) {
        const param1 = shape.parameter1;
        if (!isNaN(param1) && param1 >= 0) p1 = param1;  // can't be negative or NaN
      }
      p1 = Math.min(p1, w / 2);
      p1 = Math.min(p1, h / 2);  // limit by whole height or by half height?
      const geo = new go.Geometry();
      // a single figure consisting of straight lines and quarter-circle arcs
      geo.add(new go.PathFigure(0, p1)
        .add(new go.PathSegment(go.PathSegment.Arc, 180, 90, p1, p1, p1, p1))
        .add(new go.PathSegment(go.PathSegment.Line, w - p1, 0))
        .add(new go.PathSegment(go.PathSegment.Arc, 270, 90, w - p1, p1, p1, p1))
        .add(new go.PathSegment(go.PathSegment.Line, w, h))
        .add(new go.PathSegment(go.PathSegment.Line, 0, h).close()));
      // don't intersect with two top corners when used in an "Auto" Panel
      geo.spot1 = new go.Spot(0, 0, 0.3 * p1, 0.3 * p1);
      geo.spot2 = new go.Spot(1, 1, -0.3 * p1, 0);
      return geo;
    });

    go.Shape.defineFigureGenerator('RoundedBottomRectangle', function (shape: go.Shape, w: number, h: number) {
      // this figure takes one parameter, the size of the corner
      let p1 = 5;  // default corner size
      if (shape !== null) {
        const param1 = shape.parameter1;
        if (!isNaN(param1) && param1 >= 0) p1 = param1;  // can't be negative or NaN
      }
      p1 = Math.min(p1, w / 2);
      p1 = Math.min(p1, h / 2);  // limit by whole height or by half height?
      const geo = new go.Geometry();
      // a single figure consisting of straight lines and quarter-circle arcs
      geo.add(new go.PathFigure(0, 0)
        .add(new go.PathSegment(go.PathSegment.Line, w, 0))
        .add(new go.PathSegment(go.PathSegment.Line, w, h - p1))
        .add(new go.PathSegment(go.PathSegment.Arc, 0, 90, w - p1, h - p1, p1, p1))
        .add(new go.PathSegment(go.PathSegment.Line, p1, h))
        .add(new go.PathSegment(go.PathSegment.Arc, 90, 90, p1, h - p1, p1, p1).close()));
      // don't intersect with two bottom corners when used in an "Auto" Panel
      geo.spot1 = new go.Spot(0, 0, 0.3 * p1, 0);
      geo.spot2 = new go.Spot(1, 1, -0.3 * p1, -0.3 * p1);
      return geo;
    });

  }

  // Copied from Figures.ts
  private initFigures() {

    go.Shape.defineFigureGenerator('LogicXor', (shape, w, h) => {
      const geo = new go.Geometry()
        .add(new go.PathFigure(.5 * w, 0, false)
          .add(new go.PathSegment(go.PathSegment.Line, .5 * w, h))
          .add(new go.PathSegment(go.PathSegment.Move, 0, .5 * h))
          .add(new go.PathSegment(go.PathSegment.Line, w, .5 * h))
          .add(new go.PathSegment(go.PathSegment.Arc, 0, 360, .5 * w, .5 * h, .5 * w, .5 * h)));
      geo.defaultStretch = go.GraphObject.Uniform;
      return geo;
    });

    go.Shape.defineFigureGenerator('FivePointedStar', (shape, w, h) => {
      const starPoints = createStar(5);
      const geo = new go.Geometry();
      const fig = new go.PathFigure(starPoints[0].x * w, starPoints[0].y * h, true);
      geo.add(fig);

      for (let i = 1; i < 10; i++) {
        fig.add(new go.PathSegment(go.PathSegment.Line, starPoints[i].x * w, starPoints[i].y * h));
      }
      fig.add(new go.PathSegment(go.PathSegment.Line, starPoints[0].x * w, starPoints[0].y * h).close());
      freeArray(starPoints);
      geo.spot1 = new go.Spot(.266, .333);
      geo.spot2 = new go.Spot(.733, .733);
      return geo;
    });

    go.Shape.defineFigureGenerator('BpmnActivityLoop', (shape, w, h) => {
      const geo = new go.Geometry();
      const r = .5;
      const cx = 0; // offset from Center x
      const cy = 0; // offset from Center y
      const d = r * KAPPA;
      const mx1 = (.4 * Math.SQRT2 / 2 + .5);
      const my1 = (.5 - .5 * Math.SQRT2 / 2);
      const x1 = 1;
      const y1 = .5;
      const x2 = .5;
      const y2 = 0;
      const fig = new go.PathFigure(mx1 * w, (1 - my1) * h, false);
      geo.add(fig);

      fig.add(new go.PathSegment(go.PathSegment.Bezier, x1 * w, y1 * h, x1 * w, .7 * h,
        x1 * w, y1 * h));
      fig.add(new go.PathSegment(go.PathSegment.Bezier, (x2 + cx) * w, (y2 + cx) * h, (.5 + r + cx) * w, (.5 - d + cx) * h,
        (.5 + d + cx) * w, (.5 - r + cx) * h));
      fig.add(new go.PathSegment(go.PathSegment.Bezier, (.5 - r + cx) * w, (.5 + cy) * h, (.5 - d + cx) * w, (.5 - r + cy) * h,
        (.5 - r + cx) * w, (.5 - d + cy) * h));
      fig.add(new go.PathSegment(go.PathSegment.Bezier, (.35 + cx) * w, .9 * h, (.5 - r + cx) * w, (.5 + d + cy) * h,
        (.5 - d + cx) * w, .9 * h));
      // Arrowhead
      fig.add(new go.PathSegment(go.PathSegment.Move, (.25 + cx) * w, 0.8 * h));
      fig.add(new go.PathSegment(go.PathSegment.Line, (.35 + cx) * w, 0.9 * h));
      fig.add(new go.PathSegment(go.PathSegment.Line, (.20 + cx) * w, 0.95 * h));
      return geo;
    });

    go.Shape.defineFigureGenerator('Delete', (shape, w, h) => {
      const geo = new go.Geometry();
      const radius = .5;
      const cpOffset = KAPPA * .5;
      const centerx = .5;
      const centery = .5;
      const fig = new go.PathFigure((centerx - radius) * w, centery * h, true);
      geo.add(fig);

      fig.add(new go.PathSegment(go.PathSegment.Bezier, centerx * w, (centery - radius) * h, (centerx - radius) * w, (centery - cpOffset) * h,
        (centerx - cpOffset) * w, (centery - radius) * h));
      fig.add(new go.PathSegment(go.PathSegment.Bezier, (centerx + radius) * w, centery * h, (centerx + cpOffset) * w, (centery - radius) * h,
        (centerx + radius) * w, (centery - cpOffset) * h));
      fig.add(new go.PathSegment(go.PathSegment.Bezier, centerx * w, (centery + radius) * h, (centerx + radius) * w, (centery + cpOffset) * h,
        (centerx + cpOffset) * w, (centery + radius) * h));
      fig.add(new go.PathSegment(go.PathSegment.Bezier, (centerx - radius) * w, centery * h, (centerx - cpOffset) * w, (centery + radius) * h,
        (centerx - radius) * w, (centery + cpOffset) * h));
      const fig2 = new go.PathFigure(0.15 * w, 0.5 * h, false);
      geo.add(fig2);
      fig2.add(new go.PathSegment(go.PathSegment.Line, 0.85 * w, 0.5 * h));
      return geo;
    });
  }
}

// Copied from Figures.ts
/**
 * @hidden @internal
 * This FigureParameter class describes various properties each parameter uses in figures.
 */
export class FigureParameter {
  public static definedParameters: {[key: string]: Array<FigureParameter>} = {};
  private _name: string;
  private _defaultValue: number;
  private _minimum: number;
  private _maximum: number;
  constructor(name: string, def: number, min?: number, max?: number) {
    if (min === undefined) min = 0.0;
    if (max === undefined) max = Infinity;
    this._name = name;
    this._defaultValue = def;
    this._minimum = min;
    this._maximum = max;
    // (go.Shape as any)['_FigureParameters'] = {};
  }

  /**
   * Gets or sets the name of the figure.
   */
  get name(): string { return this._name; }
  set name(val: string) {
    if (typeof val !== 'string' || val === '') throw new Error('Shape name must be a valid string.');
    this._name = val;
  }

  /**
   * Gets or sets the default value for the parameter.
   */
  get defaultValue(): number { return this._defaultValue; }
  set defaultValue(val: number) {
    if (typeof val !== 'number' || isNaN(val)) throw new Error('The default value must be a real number, not: ' + val);
    this._defaultValue = val;
  }

  /**
   * Gets or sets the minimum value allowed for the figure parameter.
   */
  get minimum(): number { return this._minimum; }
  set minimum(val: number) {
    if (typeof val !== 'number' || isNaN(val)) throw new Error('Minimum must be a real number, not: ' + val);
    this._minimum = val;
  }

  /**
   * Gets or sets the maximum value allowed for the figure parameter.
   */
  get maximum(): number { return this._maximum; }
  set maximum(val: number) {
    if (typeof val !== 'number' || isNaN(val)) throw new Error('Maximum must be a real number, not: ' + val);
    this._maximum = val;
  }

  /**
   * This static function gets a FigureParameter for a particular figure name.
   * @param {String} figurename
   * @param {number} index, currently must be either 0 or 1
   * @return {FigureParameter}
   */
  public static getFigureParameter(figurename: string, index: number): FigureParameter | null {
    // const arr = (go.Shape as any)['_FigureParameters'][figurename];
    const arr = FigureParameter.definedParameters[figurename];
    if (!arr) return null;
    return arr[index];
  }

  /**
   * This static function sets a FigureParameter for a particular figure name.
   * @param {String} figurename
   * @param {number} index, currently must be either 0 or 1
   * @param {FigureParameter} figparam
   */
  public static setFigureParameter(figurename: string, index: number, figparam: FigureParameter) {
    if (!(figparam instanceof FigureParameter)) throw new Error('Third argument to FigureParameter.setFigureParameter is not FigureParameter: ' + figparam);
    if (figparam.defaultValue < figparam.minimum || figparam.defaultValue > figparam.maximum) {
      throw new Error('defaultValue must be between minimum and maximum, not: ' + figparam.defaultValue);
    }
    // const paramObj = (go.Shape as any)['_FigureParameters'];
    // let arr = paramObj[figurename];
    let arr: Array<FigureParameter> = FigureParameter.definedParameters[figurename];
    if (!arr) {
      // arr = [];
      // paramObj[figurename] = arr;
      arr = [];
      FigureParameter.definedParameters[figurename] = arr;
    }
    arr[index] = figparam;
  }
}
/** @ignore */
const _CachedPoints: Array<go.Point> = [];

/**
 * @ignore
 * @param {number} x
 * @param {number} y
 * @return {Point}
 */
function tempPointAt(x: number, y: number): go.Point {
  const temp = _CachedPoints.pop();
  if (temp === undefined) return new go.Point(x, y);
  temp.x = x;
  temp.y = y;
  return temp;
}

/**
 * @ignore
 * @return {Point}
 */
function tempPoint(): go.Point {
  const temp = _CachedPoints.pop();
  if (temp === undefined) return new go.Point();
  return temp;
}

/**
 * @ignore
 * @param {Point} temp
 */
function freePoint(temp: go.Point) {
  _CachedPoints.push(temp);
}

/**
 * @ignore
 * @param {number} p1x
 * @param {number} p1y
 * @param {number} p2x
 * @param {number} p2y
 * @param {number} q1x
 * @param {number} q1y
 * @param {number} q2x
 * @param {number} q2y
 * @param {Point} result
 * @return {Point}
 */
function getIntersection(p1x: number, p1y: number, p2x: number, p2y: number, q1x: number, q1y: number, q2x: number, q2y: number, result: go.Point): go.Point {
  const dx1 = p1x - p2x;
  const dx2 = q1x - q2x;
  let x;
  let y;

  if (dx1 === 0 || dx2 === 0) {
    if (dx1 === 0) {
      const m2 = (q1y - q2y) / dx2;
      const b2 = q1y - m2 * q1x;
      x = p1x;
      y = m2 * x + b2;
    } else {
      const m1 = (p1y - p2y) / dx1;
      const b1 = p1y - m1 * p1x;
      x = q1x;
      y = m1 * x + b1;
    }
  } else {
    const m1 = (p1y - p2y) / dx1;
    const m2 = (q1y - q2y) / dx2;
    const b1 = p1y - m1 * p1x;
    const b2 = q1y - m2 * q1x;

    x = (b2 - b1) / (m1 - m2);
    y = m1 * x + b1;
  }

  result.x = x;
  result.y = y;
  return result;
}

/**
 * @ignore
 * @param {number} startx
 * @param {number} starty
 * @param {number} c1x
 * @param {number} c1y
 * @param {number} c2x
 * @param {number} c2y
 * @param {number} endx
 * @param {number} endy
 * @param {number} fraction
 * @param {Point} curve1cp1  // modified result control point
 * @param {Point} curve1cp2  // modified result control point
 * @param {Point} midpoint  // modified result
 * @param {Point} curve2cp1  // modified result control point
 *  @param {Point} curve2cp2  // modified result control point
 */
function breakUpBezier(startx: number, starty: number, c1x: number, c1y: number, c2x: number, c2y: number, endx: number, endy: number, fraction: number,
                       curve1cp1: go.Point, curve1cp2: go.Point, midpoint: go.Point, curve2cp1: go.Point, curve2cp2: go.Point) {
  const fo = 1 - fraction;
  const so = fraction;
  const m1x = (startx * fo + c1x * so);
  const m1y = (starty * fo + c1y * so);
  const m2x = (c1x * fo + c2x * so);
  const m2y = (c1y * fo + c2y * so);
  const m3x = (c2x * fo + endx * so);
  const m3y = (c2y * fo + endy * so);
  const m12x = (m1x * fo + m2x * so);
  const m12y = (m1y * fo + m2y * so);
  const m23x = (m2x * fo + m3x * so);
  const m23y = (m2y * fo + m3y * so);
  const m123x = (m12x * fo + m23x * so);
  const m123y = (m12y * fo + m23y * so);

  curve1cp1.x = m1x;
  curve1cp1.y = m1y;

  curve1cp2.x = m12x;
  curve1cp2.y = m12y;

  midpoint.x = m123x;
  midpoint.y = m123y;

  curve2cp1.x = m23x;
  curve2cp1.y = m23y;

  curve2cp2.x = m3x;
  curve2cp2.y = m3y;
}

const GeneratorEllipseSpot1 = new go.Spot(0.156, 0.156);

const GeneratorEllipseSpot2 = new go.Spot(0.844, 0.844);

const KAPPA = 4 * ((Math.sqrt(2) - 1) / 3);

/** @ignore */
const _CachedArrays: Array<any> = [];

/**
 * @ignore
 * @return {Array}
 */
function tempArray(): Array<any> {
  const temp = _CachedArrays.pop();
  if (temp === undefined) return [];
  return temp;
}

/**
 * @ignore
 * @param {Array} a
 */
function freeArray(a: Array<any>) {
  a.length = 0;  // clear any references to objects
  _CachedArrays.push(a);
}


/**
 * @ignore
 * This allocates a temporary Array that should be freeArray()'ed by the caller.
 * @param {number} sides
 * @return {Array}
 */
function createPolygon(sides: number): Array<any> {
  // Point[] points = new Point[sides + 1];
  const points = tempArray();
  const radius = .5;
  const center = .5;
  const offsetAngle = Math.PI * 1.5;
  let angle = 0;

  // Loop through each side of the polygon
  for (let i = 0; i < sides; i++) {
    angle = 2 * Math.PI / sides * i + offsetAngle;
    points[i] = new go.Point((center + radius * Math.cos(angle)), (center + radius * Math.sin(angle)));
  }

  // Add the last line
  // points[points.length - 1] = points[0];
  points.push(points[0]);
  return points;
}

/**
 * @ignore
 * This allocates a temporary Array that should be freeArray()'ed by the caller.
 * @param {number} points
 * @return {Array}
 */
function createBurst(points: number): Array<any> {
  const star = createStar(points);
  const pts = tempArray(); // new Point[points * 3 + 1];

  pts[0] = star[0];
  for (let i = 1, count = 1; i < star.length; i += 2, count += 3) {
    pts[count] = star[i];
    pts[count + 1] = star[i];
    pts[count + 2] = star[i + 1];
  }

  freeArray(star);
  return pts;
}

/**
 * @ignore
 * This allocates a temporary Array that should be freeArray()'ed by the caller.
 * @param {number} points
 * @return {Array}
 */
function createStar(points: number): Array<any> {
  // First, create a regular polygon
  const polygon = createPolygon(points);
  // Calculate the points inbetween
  const pts = tempArray(); // new Point[points * 2 + 1];

  const half = Math.floor(polygon.length / 2);
  const count = polygon.length - 1;
  const offset = (points % 2 === 0) ? 2 : 1;

  for (let i = 0; i < count; i++) {
    // Get the intersection of two lines
    const p0 = polygon[i];
    const p1 = polygon[i + 1];
    const q21 = polygon[(half + i - 1) % count];
    const q2off = polygon[(half + i + offset) % count];
    pts[i * 2] = p0;
    pts[i * 2 + 1] = getIntersection(p0.x, p0.y,
      q21.x, q21.y,
      p1.x, p1.y,
      q2off.x, q2off.y, new go.Point());  // ?? not currently managed
  }

  pts[pts.length] = pts[0];

  freeArray(polygon);
  return pts;
}


